import React, { useContext, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Grid,
  Modal,
  TextField,
  Typography,
  Button,
  IconButton,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  useTheme,
  Tooltip,
  Divider
} from "@mui/material";
import { postNotification } from "../../../components/utilities/Notifications";

import * as Yup from "yup";
import { FieldArray, Formik } from "formik";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import {
  getArmyPickerArmies,
  handlePagingAll
} from "../../../components/api/APIUtilities";
import ArmyPicker from "../../../components/utilities/ArmyPicker";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { BCPTrash, BCPXmark } from "../../../components/icons/bcp/BCPIcons";
import {
  getData,
  postData
} from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import ImageUploader, {
  uploadBas64ToS3
} from "../../../components/utilities/ImageUploader";
import { ProgressModal } from "./SubComponents/EventPairingModals";
import { useConfirm } from "material-ui-confirm";
import DeckSelection from "./SubComponents/DeckSelectionComponent";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");

const buildArmyDict = (armies) => {
  let armyDict = {};
  if (armies?.length) {
    for (let army of armies) {
      let subFactions = [];
      if (army.subFactions?.length) {
        for (let subFaction of army.subFactions) {
          subFactions.push({ value: subFaction.id, label: subFaction.name });
        }

        subFactions.sort((a, b) => {
          var labelA = a.label.toLowerCase();
          var labelB = b.label.toLowerCase();
          if (labelA > labelB) return 1;
          if (labelA < labelB) return -1;
          return 0;
        });
      }
      armyDict[army.id] = subFactions;

      if (army.childArmies?.length) {
        for (let childArmy of army.childArmies) {
          subFactions = [];
          if (childArmy.subFactions?.length) {
            for (let subFaction of childArmy.subFactions) {
              subFactions.push({
                value: subFaction.id,
                label: subFaction.name
              });
            }

            subFactions.sort((a, b) => {
              var labelA = a.label.toLowerCase();
              var labelB = b.label.toLowerCase();
              if (labelA > labelB) return 1;
              if (labelA < labelB) return -1;
              return 0;
            });
          }
          armyDict[childArmy.id] = subFactions;
        }
      }
    }
  }

  return armyDict;
};

export default function ArmyListComponent(props) {
  const {
    label,
    organize,
    event,
    player,
    armyListToken,
    setPlayer,
    modal,
    open,
    setOpen,
    setRefreshPlayers,
    onClose,
    setSavedPlayer,
    active,
    tArmyList
  } = props;

  var authContext = useContext(AuthContext);
  const [armies, setArmies] = useState(props.armies || null);
  const [armyDict, setArmyDict] = useState(
    props.armyDict || buildArmyDict(props.armies)
  );

  const [armyList, setArmyList] = useState(null);
  const [loading, setLoading] = useState(true);

  const [armyName, setArmyName] = useState(
    player?.faction?.name || player?.armyName
  );
  const [characters, setCharacters] = useState(null);

  useEffect(() => {
    const check = async () => {
      if (!loading) setLoading(true);

      if (event?.gameSystem?.hasFactions) {
        let filters = {};

        if (active) {
          filters.active = true;
        }

        let apiArmies = props?.armies;
        let newDict = {};

        if (!apiArmies) {
          const { armyArray, subFactionDict } = await getArmyPickerArmies(
            event?.gameSystem?.id || event?.gameSystemId,
            filters
          );

          apiArmies = armyArray;
          newDict = subFactionDict;
        }

        // Checks if player's army is among the armies listed, if not, adds it to the list
        if (player && (player?.armyId || player?.factionId)) {
          let isArmyAbsent = true;

          apiArmies.forEach((army) => {
            if (army.id === player.armyId || player?.factionId) {
              isArmyAbsent = false;
              return;
            }
          });

          if (isArmyAbsent) {
            const playerArmy = await getData(
              `/armies/${player.armyId || player?.factionId}`
            );

            apiArmies.unshift(playerArmy);
          }
        }

        if (props?.armies && apiArmies?.length) {
          let _armyDict = buildArmyDict(apiArmies);
          newDict = {
            ...newDict,
            ..._armyDict
          };
        }

        setArmies(apiArmies);
        setArmyDict(newDict);
      }

      if (!player) {
        setArmyList(null);
        setLoading(false);
        return;
      }

      setArmyName(player?.faction?.name);

      if (player?.armyListObjectId || player?.listId) {
        var tempArmyList;
        if (
          player.armyListText ||
          player.armyListImages ||
          player.armyListXWSText
        ) {
          setArmyList({
            armyListText: player.armyListText,
            armyListImages: player.armyListImages,
            armyListXWSText: player.armyListXWSText
          });
        } else {
          try {
            var queryParams = {};
            if (armyListToken) {
              queryParams.armyListToken = armyListToken;
            }

            tempArmyList = await getData(
              `/armylists/${player?.armyListObjectId || player?.listId}`,
              {
                queryStringParameters: {
                  ...queryParams
                }
              }
            );

            setArmyList({
              armyListText: tempArmyList.armyListText,
              armyListImages: tempArmyList.armyListImages,
              armyListXWSText: tempArmyList.armyListXWSText
            });
          } catch (err) {
            setArmyList(null);
            postNotification("error", err);
          }
        }
      } else {
        setArmyList(null);
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [player]);

  useEffect(() => {
    const check = async () => {
      if (!armyName) {
        setCharacters([]);
        return;
      }

      var apiCharacters = await handlePagingAll("/cards", {
        gameSystemId: event?.gameSystem?.id || event?.gameSystemId,
        faction: armyName,
        cardType: COMPANY_FLAG === "shard-bugs" ? "Cosmic Avatar" : "Character"
      });
      setCharacters(
        apiCharacters.map((el) => {
          return {
            value: el.id,
            label: el.name
          };
        })
      );
    };

    if (!event?.gameSystem?.supportsCharacters) {
      return;
    }

    setCharacters(null);
    check();

    // eslint-disable-next-line
  }, [armyName]);

  return (
    <>
      {modal ? (
        <ArmyListComponentModal
          authContext={authContext}
          label={label}
          event={event}
          setArmyName={setArmyName}
          armies={armies}
          armyDict={armyDict}
          armyList={armyList}
          characters={characters}
          player={player}
          armyListToken={armyListToken}
          organize={organize}
          setLoading={setLoading}
          loading={loading}
          setPlayer={setPlayer}
          setRefreshPlayers={setRefreshPlayers}
          modal={modal}
          open={open}
          setOpen={setOpen}
          onClose={onClose}
          setSavedPlayer={setSavedPlayer}
          tArmyList={tArmyList}
        />
      ) : (
        <ArmyListComponentBody
          authContext={authContext}
          label={label}
          event={event}
          setArmyName={setArmyName}
          armies={armies}
          armyDict={armyDict}
          armyList={armyList}
          characters={characters}
          player={player}
          armyListToken={armyListToken}
          organize={organize}
          setLoading={setLoading}
          setPlayer={setPlayer}
          setRefreshPlayers={setRefreshPlayers}
          modal={modal}
          setOpen={setOpen}
          onClose={onClose}
          setSavedPlayer={setSavedPlayer}
          tArmyList={tArmyList}
          loading={loading}
        />
      )}

      {loading && !modal && <ModalLoadingFull />}
    </>
  );
}

const ArmyListComponentBody = (props) => {
  const {
    authContext,
    label,
    event,
    setArmyName,
    armies,
    armyDict,
    armyList,
    characters,
    player,
    armyListToken,
    organize,
    setLoading,
    setPlayer,
    setRefreshPlayers,
    modal,
    setOpen,
    onClose,
    setSavedPlayer,
    tArmyList
  } = props;

  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);

  const [listType, setListType] = useState(
    armyList?.armyListText
      ? "text"
      : armyList?.armyListImages
        ? "image"
        : "text"
  );

  const confirm = useConfirm();
  const theme = useTheme();

  async function getImageArray(images) {
    if (!images || !images.length) {
      return null;
    } else {
      let imageIdArr = [];
      let index = 0;

      let perc = 0;
      let step = 100 / (images.length - imageIdArr.length);

      setProgress(0);
      setProcessing(true);

      while (images.length !== imageIdArr.length) {
        const newListImage = await uploadBas64ToS3(images[index]?.imageURL);

        if (newListImage?.fileId) {
          imageIdArr.push(newListImage.fileId);
        }
        perc += step;
        setProgress(perc < 1 ? 1 : perc);
        index++;
      }

      setProcessing(false);

      return imageIdArr;
    }
  }

  return (
    <Grid container sx={{ gap: 2 }}>
      <Grid
        item
        xs={12}
        sx={{
          p: [0, 2]
        }}
      >
        {event?.gameSystem?.supportsDeckBuilder && (
          <DeckSelection
            label={
              label ||
              event?.gameSystem?.listLabel ||
              tArmyList("fallbacks.list")
            }
            labelVariant="h3"
            authContext={authContext}
            event={event}
            player={{ ...player, armyList: armyList || player.armyList }}
            processing={processing}
            setProcessing={setProcessing}
            handleSetPlayer={(e) => {
              setListType("");
              setPlayer(e);
            }}
          />
        )}
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          p: [0, 2]
        }}
      >
        <Typography variant="h3" paragraph>
          {event?.gameSystem?.supportsDeckBuilder
            ? tArmyList("legacyLists", {
                listLabel:
                  event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
              })
            : `${label || event?.gameSystem?.listLabel || tArmyList("fallbacks.list")}`}
        </Typography>

        <Formik
          initialValues={{
            armyId:
              player?.armyId || player?.factionId || player?.faction?.id || "",
            subFactionId: player?.subFactionId || player?.subFaction?.id || "",
            characterId: player?.characterId || "",

            listText: armyList?.armyListText || "",
            listImages: armyList?.armyListImages || [],
            listImageFiles: armyList?.armyListImages?.map((el) => {
              return { imageURL: el };
            }) || [{}],
            xws: armyList?.armyListXWSText || []
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            // TODO: Add a check to see if the user has selected either a text or image and then add a required check depending on selected type
            listText:
              listType === "text" &&
              Yup.string()
                .min(3)
                .required(tArmyList("validationErrors.required")),
            listImageFiles:
              listType === "image" &&
              Yup.array()
                .min(1)
                .required(tArmyList("validationErrors.required"))
          })}
          onSubmit={async (values) => {
            let body = {
              playerId: player.id,
              sendNotification: !organize,
              listInfo: {}
            };
            if (armyListToken) {
              body.armyListToken = armyListToken;
            }
            if (values.armyId && values.armyId !== "") {
              body.armyId = values.armyId;
            }
            if (values.subFactionId && values.subFactionId !== "") {
              body.subFactionId = values.subFactionId;
            }
            if (values.characterId && values.characterId !== "") {
              body.characterId = values.characterId;
            }
            try {
              setLoading(true);

              if (listType === "image") {
                if (values?.listImageFiles && values.listImageFiles.length) {
                  values.listImageFiles = values.listImageFiles.filter(
                    (image) => image?.imageURL
                  );

                  if (values?.listImageFiles && values.listImageFiles.length) {
                    body.listInfo.listImageFiles = await getImageArray(
                      values?.listImageFiles
                    );
                  } else {
                    values.listImageFiles = [{}];
                    throw new Error(
                      tArmyList("validationErrors.noImagesFound")
                    );
                  }
                }
              } else if (listType === "text") {
                if (values?.listText && values?.listText.trim() !== "") {
                  body.listInfo.listText = values.listText;
                }
              }
              const apiData = await postData(`/armylists`, body);

              if (apiData?.id) {
                if (setPlayer) {
                  setPlayer({
                    ...player,
                    armyListObjectId: apiData.id,
                    listId: apiData.id,
                    listStatus: apiData.listStatus || player.listStatus,
                    armyId: apiData.armyId,
                    armyName: apiData.army?.name,
                    subFactionId: apiData.subFactionId,
                    subFactionName: apiData.subFaction?.name,
                    characterId: apiData.characterId,
                    characterName: apiData.character?.name
                  });
                }
                if (setSavedPlayer) {
                  setSavedPlayer({
                    ...player,
                    armyListObjectId: apiData.id,
                    listId: apiData.id,
                    listStatus: apiData.listStatus || player.listStatus,
                    armyId: apiData.armyId,
                    armyName: apiData.army?.name,
                    subFactionId: apiData.subFactionId,
                    subFactionName: apiData.subFaction?.name,
                    characterId: apiData.characterId,
                    characterName: apiData.character?.name
                  });
                }
              }
              postNotification(
                "success",
                tArmyList("notifications.success.listUploadSuccessful", {
                  listLabel:
                    event?.gameSystem?.listLabel || tArmyList("fallbacks.list")
                })
              );
              if (setRefreshPlayers) setRefreshPlayers(true);
              if (modal && setOpen) {
                if (onClose) onClose();
                setOpen(false);
              }
            } catch (err) {
              if (err.response?.data?.error === "") {
                postNotification(
                  "error",
                  tArmyList("notifications.errors.oneUseLink", {
                    listLabel:
                      event?.gameSystem?.listLabel ||
                      tArmyList("fallbacks.list")
                  })
                );
              } else {
                postNotification("error", err);
              }
            }
            setLoading(false);
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                {event?.gameSystem?.hasFactions && (
                  <>
                    <Grid item xs={12}>
                      <ArmyPicker
                        armies={armies}
                        name="armyId"
                        label={
                          event?.gameSystem?.factionLabel ||
                          tArmyList("fallbacks.faction")
                        }
                        value={values.armyId}
                        handleChange={(e) => {
                          handleChange(e);
                          let army = armies.filter(
                            (el) => el.id === e.target.value
                          )[0];
                          setArmyName(army?.name);
                          setFieldValue("subFactionId", "");
                          setFieldValue("characterId", "");
                        }}
                        error={Boolean(touched.armyId && errors.armyId)}
                        helperText={touched.armyId && errors.armyId}
                        clearable
                      />
                    </Grid>

                    {event?.gameSystem?.hasSubFactions ||
                    event?.gameSystem?.subFactionRequired ? (
                      <Grid item xs={12}>
                        <GenericPicker
                          name="subFactionId"
                          label={
                            event?.gameSystem?.subFactionLabel ||
                            tArmyList("fallbacks.subFaction")
                          }
                          value={values.subFactionId}
                          handleChange={handleChange}
                          inclAny={false}
                          clearable
                          list={armyDict?.[values.armyId] || []}
                          error={Boolean(
                            touched.subFactionId && errors.subFactionId
                          )}
                          helperText={
                            touched.subFactionId && errors.subFactionId
                          }
                          disabled={!values.armyId || values.armyId === ""}
                          required={
                            event?.gameSystem?.subFactionRequired || false
                          }
                        />
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </>
                )}

                {event?.gameSystem?.supportsCharacters ? (
                  <Grid item xs={12}>
                    {characters ? (
                      <GenericPicker
                        name="characterId"
                        label={
                          event?.gameSystem?.characterLabel ||
                          tArmyList("fallbacks.character")
                        }
                        value={values.characterId}
                        handleChange={handleChange}
                        inclAny={false}
                        clearable
                        list={characters || []}
                        error={Boolean(
                          touched.characterId && errors.characterId
                        )}
                        helperText={touched.characterId && errors.characterId}
                        disabled={!values.armyId || values.armyId === ""}
                      />
                    ) : (
                      <TextField
                        disabled
                        label={
                          event?.gameSystem?.characterLabel ||
                          tArmyList("fallbacks.character")
                        }
                        color="primary"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CircularProgress />
                            </InputAdornment>
                          )
                        }}
                      />
                    )}
                  </Grid>
                ) : (
                  <></>
                )}

                {event?.listOptions?.allowsImages && (
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormHelperText>
                          {tArmyList("helpText.selectSubmissionMethod", {
                            listLabel:
                              event?.gameSystem?.listLabel ||
                              tArmyList("fallbacks.list")
                          })}
                        </FormHelperText>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={
                            listType === "text" ? "contained" : "outlined"
                          }
                          onClick={() => {
                            setListType("text");
                          }}
                        >
                          {tArmyList("text")}
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={
                            listType === "image" ? "contained" : "outlined"
                          }
                          onClick={() => {
                            setFieldValue("listImageFiles", []);
                            setListType("image");
                          }}
                        >
                          {tArmyList("image")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {listType === "text" ? (
                  <Grid item xs={12}>
                    <FormHelperText>
                      {tArmyList("helpText.copyPasteText", {
                        listLabel:
                          event?.gameSystem?.listLabel ||
                          tArmyList("fallbacks.list")
                      })}
                    </FormHelperText>

                    <TextField
                      name="listText"
                      label={
                        event?.gameSystem?.listLabel ||
                        tArmyList("fallbacks.list")
                      }
                      value={values.listText}
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={10}
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.listText && errors.listText)}
                      helperText={touched.listText && errors.listText}
                    />
                  </Grid>
                ) : listType === "image" ? (
                  <Grid item xs={12}>
                    <FormHelperText>
                      {tArmyList("helpText.uploadImages", {
                        listLabel:
                          event?.gameSystem?.listLabel ||
                          tArmyList("fallbacks.list")
                      })}
                    </FormHelperText>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1
                      }}
                    >
                      <Typography variant="body1" gutterBottom>
                        {values.listImageFiles?.length > 1
                          ? tArmyList("listImages")
                          : tArmyList("listImage")}
                      </Typography>
                      <Tooltip
                        title={tArmyList("clearAllImages")}
                        placement="top"
                      >
                        <IconButton
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={async () => {
                            await confirm({
                              title: tArmyList("confirmClearImages"),
                              description: tArmyList(
                                "confirmClearImagesDescription"
                              ),
                              dialogProps: {
                                sx: {
                                  zIndex: 99999
                                }
                              }
                            });
                            setFieldValue("listImageFiles", []);
                          }}
                        >
                          <BCPTrash
                            sx={{ color: theme?.palette?.error.main }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Grid container spacing={1}>
                      <FieldArray
                        name="listImageFiles"
                        render={(arrayHelpers) => (
                          <>
                            {values.listImageFiles?.length > 0 &&
                              values.listImageFiles?.map(
                                (picture, rowIndex) => {
                                  return (
                                    <>
                                      <Grid item xs={12}>
                                        <ImageUploader
                                          label={tArmyList("listImageNumber", {
                                            number: rowIndex
                                          })}
                                          name={`listImageFiles[${rowIndex}].imageURL`}
                                          setFieldValue={setFieldValue}
                                          defaultUrl={
                                            values.listImageFiles[rowIndex]
                                              ?.photo?.url ||
                                            values.listImageFiles[rowIndex]
                                              ?.imageURL
                                          }
                                          noAspect
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          fullWidth
                                          onClick={() =>
                                            arrayHelpers.remove(rowIndex)
                                          }
                                        >
                                          Remove Image
                                        </Button>
                                      </Grid>
                                    </>
                                  );
                                }
                              )}

                            {(values.listImageFiles?.length < 5 ||
                              event?.numberOfRounds === 0) && (
                              <Grid item xs={12}>
                                <Divider sx={{ mt: 1, mb: 1 }} />
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  fullWidth
                                  onClick={() => arrayHelpers.push({})}
                                >
                                  {tArmyList("addImage")}
                                </Button>
                              </Grid>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <></>
                )}

                {listType && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      color="success"
                      variant="contained"
                      type="submit"
                    >
                      {tArmyList("upload")}
                    </Button>
                  </Grid>
                )}

                <ProgressModal
                  open={processing}
                  value={progress}
                  message={tArmyList("imagesUploaded")}
                />
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

const ArmyListComponentModal = (props) => {
  const {
    authContext,
    label,
    event,
    setArmyName,
    armies,
    armyDict,
    characters,
    open,
    armyList,
    player,
    armyListToken,
    organize,
    setLoading,
    setPlayer,
    setRefreshPlayers,
    modal,
    setOpen,
    onClose,
    setSavedPlayer,
    tArmyList,
    loading
  } = props;

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        if (onClose) onClose();
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          minHeight: "95vh",
          maxHeight: "95vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            if (onClose) onClose();
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <ArmyListComponentBody
          authContext={authContext}
          label={label}
          event={event}
          setArmyName={setArmyName}
          armies={armies}
          armyDict={armyDict}
          armyList={armyList}
          characters={characters}
          player={player}
          armyListToken={armyListToken}
          organize={organize}
          setLoading={setLoading}
          setPlayer={setPlayer}
          setRefreshPlayers={setRefreshPlayers}
          modal={modal}
          setOpen={setOpen}
          onClose={onClose}
          setSavedPlayer={setSavedPlayer}
          tArmyList={tArmyList}
        />

        {loading && open && <ModalLoadingFull />}
      </Box>
    </Modal>
  );
};
